import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaCheckCircle, FaTimesCircle, FaEye, FaIdCard, FaClock } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const AdminVerifications = () => {
  const { api } = useAuth();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState('');

  useEffect(() => {
    fetchQueue();
  }, []);

  const fetchQueue = async () => {
    try {
      const res = await api.get('/api/admin/verification-queue');
      if (res.data.success) {
        setQueue(res.data.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!selectedItem) return;
    try {
      if (actionType === 'approve') {
        await api.put(`/api/admin/approve-verification/${selectedItem.id}`);
        toast.success('Verification approved');
      } else {
        await api.put(`/api/admin/reject-verification/${selectedItem.id}`, { admin_notes: adminNotes });
        toast.success('Verification rejected');
      }
      setShowModal(false);
      setSelectedItem(null);
      setAdminNotes('');
      fetchQueue();
    } catch (err) {
      toast.error('Action failed');
    }
  };

  const openAction = (item, type) => {
    setSelectedItem(item);
    setActionType(type);
    setAdminNotes('');
    setShowModal(true);
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-[#FFF8F0] pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/admin" className="text-[#8B0000] hover:underline flex items-center gap-1 text-sm mb-2">
            <FaArrowLeft /> Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-[#8B0000] font-['Playfair_Display'] mb-1">Verification Queue</h1>
          <p className="text-gray-600 font-['Noto_Sans_Devanagari'] mb-6">ओळख पडताळणी रांग</p>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#8B0000] border-t-transparent mx-auto"></div>
            </div>
          ) : queue.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md p-12 text-center">
              <FaCheckCircle className="text-5xl text-green-500 mx-auto mb-4" />
              <p className="text-xl text-gray-600">No pending verifications</p>
              <p className="text-gray-400 font-['Noto_Sans_Devanagari']">सर्व पडताळण्या पूर्ण झाल्या</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {queue.map(item => (
                <div key={item.id} className="bg-white rounded-xl shadow-md p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#FFF8F0] flex items-center justify-center flex-shrink-0">
                    <FaIdCard className="text-xl text-[#8B0000]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#8B0000]">{item.first_name} {item.last_name}</h3>
                    <p className="text-sm text-gray-600">{item.email} | {item.mobile}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      <FaClock className="inline mr-1" />
                      Submitted: {new Date(item.created_at).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {item.aadhaar_photo && (
                      <a href={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/${item.aadhaar_photo}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 flex items-center gap-1">
                        <FaEye /> Aadhaar
                      </a>
                    )}
                    {item.pan_photo && (
                      <a href={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/${item.pan_photo}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 flex items-center gap-1">
                        <FaEye /> PAN
                      </a>
                    )}
                    <button onClick={() => openAction(item, 'approve')} className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1">
                      <FaCheckCircle /> Approve
                    </button>
                    <button onClick={() => openAction(item, 'reject')} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-1">
                      <FaTimesCircle /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Action Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-[#8B0000] mb-4">
              {actionType === 'approve' ? 'Approve Verification' : 'Reject Verification'}
            </h3>
            <p className="text-gray-600 mb-4">
              {actionType === 'approve'
                ? `Approve verification for ${selectedItem?.first_name} ${selectedItem?.last_name}?`
                : `Reject verification for ${selectedItem?.first_name} ${selectedItem?.last_name}?`
              }
            </p>
            {actionType === 'reject' && (
              <textarea
                value={adminNotes}
                onChange={e => setAdminNotes(e.target.value)}
                placeholder="Reason for rejection..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#8B0000] mb-4"
                rows="3"
              />
            )}
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleAction} className={`px-4 py-2 text-white rounded-lg ${actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
                {actionType === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default AdminVerifications;
